import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ArrowLeft } from 'lucide-react';
import { buttonVariants } from '../components/ui/button';
import { PublicShell } from '../components/PublicShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { publicUrl, hreflangAlternates, activeLang, ogLocaleOf } from '../lib/publicSeo';

const SECTIONS = [
  'shutdownNotice',
  'dataOwnership',
  'bigChanges',
] as const;

type SectionKey = (typeof SECTIONS)[number];

const ROUTE = '/promise';
const OG_IMAGE = 'https://markflow.fyi/og.png';

export function PromisePage() {
  const { t, i18n } = useTranslation();
  const lang = activeLang(i18n.resolvedLanguage);
  const pageUrl = publicUrl(ROUTE, lang);

  usePageMeta({
    title: t('promise.meta.title'),
    description: t('promise.meta.description'),
    canonical: pageUrl,
    ogTitle: t('promise.meta.ogTitle'),
    ogDescription: t('promise.meta.ogDescription'),
    ogImage: OG_IMAGE,
    ogUrl: pageUrl,
    htmlLang: lang,
    ogLocale: ogLocaleOf(lang),
    ogLocaleAlternate: ogLocaleOf(lang === 'uk' ? 'en' : 'uk'),
    alternates: hreflangAlternates(ROUTE),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: t('promise.meta.title'),
      description: t('promise.meta.description'),
      url: pageUrl,
    },
  });

  const renderSection = (key: SectionKey) => (
    <section key={key} className="space-y-3">
      <h2 className="text-xl font-semibold">{t(`promise.sections.${key}.title`)}</h2>
      <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed whitespace-pre-line">
        {t(`promise.sections.${key}.body`)}
      </p>
      {key === 'dataOwnership' && (
        <Link href="/settings/account" className={buttonVariants({ variant: 'outline' })}>
          {t('promise.dataOwnershipCta')}
        </Link>
      )}
    </section>
  );

  return (
    <PublicShell>
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6">
          <Link href="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('footer.backToApp')}
          </Link>

          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {t('promise.title')}
            </h1>
          </div>
          <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed">{t('promise.intro')}</p>

          {SECTIONS.map(renderSection)}
        </div>
      </main>
    </PublicShell>
  );
}
